// useMarkArtifactSeen — клиентский сигнал «пользователь увидел артефакт» для
// presence-aware подавления email/push (см. docs/specs/presence-aware-notifications.md §3.2-3.3).
//
// Возвращает стабильную функцию ack. Используется двумя источниками:
//  - глобальным toast-listener'ом (level 'toast') — гасит только email/push;
//  - страницами-свидетелями (level 'surface') — гасит email/push И помечает
//    запись в колоколе прочитанной (через серверный markSeen).
//
// Гейт: ack НЕ отправляется, если вкладка/приложение скрыты — тогда пользователь
// «ушёл со страницы» и должен получить колокол + email/push.

import { type ArtifactKind, artifactGroupKey } from "@studyhub/shared/utils/artifact-group-key";
import { useCallback } from "react";
import { isAppVisible } from "@/lib/app-visible";
import { api } from "@/lib/trpc-client";

export type ArtifactSeenLevel = "toast" | "surface";

/**
 * Хук, возвращающий функцию `markSeen(groupKey, level)`.
 *
 * Деструктурируем именно `mutate` — в React Query v5 объект мутации НЕ стабилен
 * между рендерами (пересоздаётся), а `mutate` стабилен. Это делает возвращаемый
 * callback реально стабильным, поэтому он безопасен в deps `useSocketEvent` /
 * ручного `useEffect` (иначе эффект пере-подписывал бы сокет каждый рендер).
 */
export function useMarkArtifactSeen(): (groupKey: string, level: ArtifactSeenLevel) => void {
	const { mutate } = api.notifications.markSeen.useMutation();
	return useCallback(
		(groupKey: string, level: ArtifactSeenLevel) => {
			// Скрытая вкладка/свёрнутое приложение → не «увидел» → пропускаем ack.
			if (!isAppVisible()) return;
			if (!groupKey) return;
			mutate({ groupKey, level });
		},
		[mutate],
	);
}

/** Удобный хелпер для страниц: собирает groupKey из kind + entityId (канон-источник). */
export function markArtifactSeenKey(kind: ArtifactKind, id: string): string {
	return artifactGroupKey(kind, id);
}
